/** Client-safe plan invest-limit helpers (no Mongoose / Node APIs). */

const MAX_COUNT = 999;
const MAX_WINDOW_DAYS = 3650;

function limitFieldMissing(plan, key) {
  return !plan || plan[key] === undefined || plan[key] === null;
}

export function resolveInvestLimit(plan) {
  const configured =
    !limitFieldMissing(plan, 'investLimitMax') ||
    !limitFieldMissing(plan, 'investLimitWindowDays');
  const max = configured
    ? Math.min(Math.max(0, Math.floor(Number(plan.investLimitMax)) || 0), MAX_COUNT)
    : 0;
  const windowDays = configured
    ? Math.min(
        Math.max(0, Math.floor(Number(plan.investLimitWindowDays)) || 0),
        MAX_WINDOW_DAYS
      )
    : 0;
  return {
    max,
    windowDays,
    enabled: max > 0 && windowDays > 0,
  };
}

export function normalizeInvestLimit(maxRaw, daysRaw) {
  const max = Math.min(Math.max(0, Math.floor(Number(maxRaw)) || 0), MAX_COUNT);
  const windowDays = Math.min(
    Math.max(0, Math.floor(Number(daysRaw)) || 0),
    MAX_WINDOW_DAYS
  );
  return { investLimitMax: max, investLimitWindowDays: windowDays };
}

export function serializePlanInvestLimitForClient(plan) {
  if (!plan || typeof plan !== 'object') return plan;
  const limit = resolveInvestLimit(plan);
  return {
    ...plan,
    investLimitMax: limit.max,
    investLimitWindowDays: limit.windowDays,
    investLimitEnabled: limit.enabled,
  };
}

/** Accepts a plan (`investLimitMax` / `investLimitWindowDays`) or resolved `{ max, windowDays, enabled }`. */
export function formatInvestLimitSummary(limitOrPlan) {
  const resolved =
    limitOrPlan &&
    typeof limitOrPlan === 'object' &&
    'max' in limitOrPlan &&
    'windowDays' in limitOrPlan
      ? limitOrPlan
      : resolveInvestLimit(limitOrPlan);
  const { max, windowDays, enabled } = resolved;
  if (!enabled) return 'No limit';
  const times = max === 1 ? '1 time' : `${max} times`;
  const days = windowDays === 1 ? '1 day' : `${windowDays} days`;
  return `${times} / ${days}`;
}

export function formatInvestLimitUserLine(t, plan) {
  const { max, windowDays, enabled } = resolveInvestLimit(plan);
  if (!enabled) return '';
  return t('user.investments.card.investLimitLine', {
    count: max,
    days: windowDays,
  });
}

export function windowStartDate(windowDays) {
  return new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000);
}

export function countInvestmentsInWindowFromList(investments, planId, windowDays) {
  const since = windowStartDate(windowDays).getTime();
  return (Array.isArray(investments) ? investments : []).filter((inv) => {
    if (String(inv?.planId) !== String(planId)) return false;
    const created = inv?.createdAt ? new Date(inv.createdAt).getTime() : 0;
    return created >= since;
  }).length;
}

export function buildInvestLimitError(plan) {
  const { max, windowDays, enabled } = resolveInvestLimit(plan);
  if (!enabled) return '';
  const name = String(plan?.name || 'this plan').trim();
  const times = max === 1 ? 'once' : `${max} times`;
  const days = windowDays === 1 ? '1 day' : `${windowDays} days`;
  return `You can only invest in ${name} ${times} every ${days}.`;
}
